const DEFAULT_TIMEOUT_MS = 3000;

/**
 * Resolve the first element matching `selector`, waiting for it to mount.
 *
 * Views and teleported widgets (Naive dropdowns, modals) mount
 * asynchronously, so a wanted element may not exist yet. Watches the DOM via
 * MutationObserver and gives up after `timeoutMs` — a `null` result means
 * "skip", never "hang".
 */
export function waitForSelector(
  selector: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<HTMLElement | null> {
  return waitForCondition(() => {
    const el = document.querySelector(selector);
    return el instanceof HTMLElement ? el : null;
  }, timeoutMs);
}

/**
 * Resolve the element carrying `data-tour="<key>"`, waiting for it to mount
 * AND be visible. Visibility matters with real user data/layouts: a target
 * inside a minimized pane or an inactive tab exists in the DOM but is
 * display:none — anchoring a scene to it would produce a broken bubble, so
 * hidden targets time out and skip exactly like missing ones.
 */
export function waitForTourElement(
  key: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<HTMLElement | null> {
  return waitForCondition(() => {
    const el = document.querySelector(`[data-tour="${key}"]`);
    return el instanceof HTMLElement && el.getClientRects().length > 0 ? el : null;
  }, timeoutMs);
}

/**
 * The same test `waitForTourElement` applies, without the waiting — for a tour
 * step's `prerequisite`, where the answer is already knowable. A scene whose
 * anchor is conditionally rendered (a permission-gated sidebar row, a control
 * that only exists on some plans) then skips instantly instead of burning the
 * resolver's full timeout first.
 */
export function hasVisibleTarget(key: string): boolean {
  const el = document.querySelector(`[data-tour="${key}"]`);
  return el instanceof HTMLElement && el.getClientRects().length > 0;
}

/**
 * Poll `probe` (against DOM mutations + a coarse interval) until it returns a
 * value or the timeout elapses. Used for lookups a CSS selector can't express
 * (e.g. "the table row whose text contains X").
 */
export function waitForCondition<T>(
  probe: () => T | null | undefined,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<T | null> {
  const initial = probe();
  if (initial !== null && initial !== undefined) {
    return Promise.resolve(initial);
  }

  return new Promise(resolve => {
    let settled = false;
    const check = (): void => {
      const result = probe();
      if (result !== null && result !== undefined) {
        finish(result);
      }
    };
    const observer = new MutationObserver(check);
    // Coarse interval as a safety net for changes MutationObserver can't see
    // (e.g. attribute-only or style-driven visibility).
    const interval = setInterval(check, 250);
    const timer = setTimeout(() => {
      finish(null);
    }, timeoutMs);

    function finish(result: T | null): void {
      if (settled) {
        return;
      }
      settled = true;
      observer.disconnect();
      clearInterval(interval);
      clearTimeout(timer);
      resolve(result);
    }

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  });
}
